import { Capacitor } from '@capacitor/core';
import { supabase } from '../supabase/client';
import { UserPreferencesService } from './userPreferencesService';
import { showForegroundPushBanner } from '../../utils/foregroundPushBanner';
import {
  getNotificationOpenPath,
  shouldShowBrowserPushNotification,
} from '../../utils/userNotificationPolicy';
import { dispatchNativePushNotification } from '../push/dispatchPushNotification';

// 檢查是否為有效的 UUID
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  createdAt: Date;
  updatedAt: Date;
  relatedId?: string;
}

export class NotificationService {
  // 獲取用戶的通知列表
  static async getUserNotifications(userId: string, limit?: number): Promise<Notification[]> {
    try {
      console.log('獲取用戶通知:', userId);
      
      // 如果 userId 不是有效的 UUID（例如 temp-user-id），跳過查詢
      if (!isValidUUID(userId)) {
        console.log('用戶ID不是有效的UUID，跳過查詢');
        return [];
      }
      
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ 獲取通知失敗:', {
          error: error.message,
          code: error.code,
          details: error.details,
          userId
        });
        return [];
      }

      console.log('✅ 成功獲取通知:', {
        userId,
        count: data?.length || 0,
        notifications: data?.map(n => ({ id: n.id, type: n.type, title: n.title }))
      });

      const notifications: Notification[] =
        data?.map(notification => ({
          id: notification.id,
          title: notification.title,
          message: notification.message,
          type: notification.type,
          isRead: notification.is_read,
          createdAt: new Date(notification.created_at),
          updatedAt: new Date(notification.updated_at),
          relatedId: notification.related_id
        })) || [];

      // 嘗試為與服務請求相關的通知增加「[工匠] / [公司]」標籤
      return await this.addProviderPrefixToNotifications(notifications);
    } catch (error) {
      console.error('Error in getUserNotifications:', error);
      return [];
    }
  }

  // 標記通知為已讀 (單個或多個)
  static async markAsRead(notificationIds: string | string[]): Promise<boolean> {
    try {
      const ids = Array.isArray(notificationIds) ? notificationIds : [notificationIds];
      
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .in('id', ids);

      if (error) {
        console.error('Error marking notification as read:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in markAsRead:', error);
      return false;
    }
  }

  // 標記所有通知為已讀
  static async markAllAsRead(userId: string): Promise<boolean> {
    try {
      // 如果 userId 不是有效的 UUID（例如 temp-user-id），跳過查詢
      if (!isValidUUID(userId)) {
        console.log('用戶ID不是有效的UUID，跳過標記已讀');
        return false;
      }

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in markAllAsRead:', error);
      return false;
    }
  }

  // 刪除通知
  static async deleteNotification(notificationId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) {
        console.error('Error deleting notification:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteNotification:', error);
      return false;
    }
  }

  // 清空所有通知
  static async clearAllNotifications(userId: string): Promise<boolean> {
    try {
      // 如果 userId 不是有效的 UUID（例如 temp-user-id），跳過查詢
      if (!isValidUUID(userId)) {
        console.log('用戶ID不是有效的UUID，跳過清空通知');
        return false;
      }

      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId);

      if (error) {
        console.error('Error clearing all notifications:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in clearAllNotifications:', error);
      return false;
    }
  }

  // 創建新通知
  static async createNotification(
    userId: string,
    type: string,
    title: string,
    message: string,
    relatedId?: string,
    options?: { skipPush?: boolean }
  ): Promise<boolean> {
    try {
      // 如果 userId 不是有效的 UUID（例如 temp-user-id），跳過創建
      if (!isValidUUID(userId)) {
        console.log('用戶ID不是有效的UUID，跳過創建通知');
        return false;
      }

      // 檢查用戶通知偏好
      const { data: preferences, error: prefError } = await supabase
        .from('user_notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      // 如果查詢失敗（表不存在或 RLS 問題），使用默認值（允許通知）
      if (prefError) {
        if (
          prefError.code !== 'PGRST116' &&
          prefError.code !== 'PGRST205' &&
          prefError.code !== 'PGRST406'
        ) {
          console.warn('無法查詢用戶通知偏好，使用默認設定:', prefError);
        }
        // 無記錄或表不可用：略過類型偏好檢查，繼續寫入站內通知
      }

      // 如果用戶關閉了該類型的通知，跳過創建
      if (preferences) {
        const preferenceKey = type as keyof typeof preferences;
        if (preferences[preferenceKey] === false) {
          console.log(`用戶已關閉 ${type} 類型的通知`);
          return false;
        }
      }

      const { data, error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          type,
          title,
          message,
          related_id: relatedId,
          is_read: false
        })
        .select();

      if (error) {
        console.error('❌ 創建通知失敗:', {
          error: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          userId,
          type,
          title
        });
        return false;
      }

      console.log('✅ 通知創建成功:', {
        notification_id: data?.[0]?.id,
        user_id: userId,
        type,
        title
      });

      if (!options?.skipPush) {
        void dispatchNativePushNotification({
          userId,
          title,
          message,
          type,
          priority: this.getNotificationPriority(type),
        });
      }

      return true;
    } catch (error) {
      console.error('Error in createNotification:', error);
      return false;
    }
  }

  /**
   * 後台驗收異議：管理員向承接工匠發送站內通知（選填給工匠看的說明）。
   * type = verification_admin_followup，與客戶異議 verification_disputed 分開以利稽核。
   */
  static async notifyCraftsmanDisputeAdminFollowup(
    craftsmanUserId: string,
    serviceRequestId: string,
    serviceTitle: string,
    adminMessage: string
  ): Promise<boolean> {
    const trimmed = adminMessage.trim();
    const body =
      trimmed.length > 0
        ? `客服留言：${trimmed.slice(0, 500)}${trimmed.length > 500 ? '…' : ''}`
        : '平台已檢視異議，請聯繫客戶或補資料。';
    const title = `【驗收異議】${serviceTitle || '工單'}：平台通知`;
    return this.createNotification(
      craftsmanUserId,
      'verification_admin_followup',
      title,
      body,
      serviceRequestId
    );
  }

  /**
   * 根據通知關聯的 service_requests 記錄，自動在標題前加上 [工匠] / [公司] 標籤
   * 注意：僅在 related_id 對應 service_requests.id 時生效，其他類型通知不受影響
   */
  private static async addProviderPrefixToNotifications(
    notifications: Notification[]
  ): Promise<Notification[]> {
    try {
      // 收集所有帶有 relatedId 的通知（通常關聯 service_requests）
      const relatedIds = Array.from(
        new Set(
          notifications
            .map((n) => n.relatedId)
            .filter((id): id is string => Boolean(id))
        )
      );

      if (relatedIds.length === 0) {
        return notifications;
      }

      const { data, error } = await supabase
        .from('service_requests')
        .select('id, craftsman_id, company_id')
        .in('id', relatedIds);

      if (error || !data) {
        if (error) {
          console.warn('為通知加上來源標籤時查詢 service_requests 失敗:', error);
        }
        return notifications;
      }

      const providerMap = new Map<
        string,
        'craftsman' | 'company'
      >();

      data.forEach((row: any) => {
        if (row?.company_id) {
          providerMap.set(row.id, 'company');
        } else if (row?.craftsman_id) {
          providerMap.set(row.id, 'craftsman');
        }
      });

      const prefixRegex = /^(\[工匠\]|\[公司\]|【工匠】|【公司】)/;

      return notifications.map((n) => {
        if (!n.relatedId) return n;
        const provider = providerMap.get(n.relatedId);
        if (!provider) return n;

        // 已經有前綴就不重複加
        if (prefixRegex.test(n.title)) return n;

        const prefix = provider === 'company' ? '[公司]' : '[工匠]';
        return {
          ...n,
          title: `${prefix} ${n.title}`,
        };
      });
    } catch (error) {
      console.error('為通知加上來源標籤時發生錯誤:', error);
      return notifications;
    }
  }

  // 獲取未讀通知數量
  static async getUnreadCount(userId: string): Promise<number> {
    try {
      // 如果 userId 不是有效的 UUID（例如 temp-user-id），跳過查詢
      if (!isValidUUID(userId)) {
        console.log('用戶ID不是有效的UUID，跳過查詢');
        return 0;
      }

      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) {
        console.error('Error getting unread count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getUnreadCount:', error);
      return 0;
    }
  }

  // 獲取通知統計
  static async getNotificationStats(userId: string): Promise<{
    total: number;
    unread: number;
    read: number;
    byType: Record<string, number>;
  }> {
    try {
      // 如果 userId 不是有效的 UUID（例如 temp-user-id），跳過查詢
      if (!isValidUUID(userId)) {
        console.log('用戶ID不是有效的UUID，跳過查詢統計');
        return { total: 0, unread: 0, read: 0, byType: {} };
      }

      const { data, error } = await supabase
        .from('notifications')
        .select('type, is_read')
        .eq('user_id', userId);

      if (error) {
        console.error('Error getting notification stats:', error);
        return { total: 0, unread: 0, read: 0, byType: {} };
      }

      const total = data?.length || 0;
      const unread = data?.filter(n => !n.is_read).length || 0;
      const read = total - unread;
      
      const byType: Record<string, number> = {};
      data?.forEach(notification => {
        byType[notification.type] = (byType[notification.type] || 0) + 1;
      });

      return { total, unread, read, byType };
    } catch (error) {
      console.error('Error in getNotificationStats:', error);
      return { total: 0, unread: 0, read: 0, byType: {} };
    }
  }

  // 格式化時間顯示
  static formatTimeAgo(date: Date): string {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return '剛剛';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}分鐘前`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}小時前`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days}天前`;
    } else if (diffInSeconds < 2592000) {
      const weeks = Math.floor(diffInSeconds / 604800);
      return `${weeks}週前`;
    } else {
      return date.toLocaleDateString('zh-TW');
    }
  }

  // 根據通知類型獲取類別標籤
  static getCategoryLabel(type: string): string {
    const categoryMap: Record<string, string> = {
      'job_new': '案件',
      'job_accepted': '案件',
      'job_rejected': '案件',
      'job_completed': '案件',
      'job_cancelled': '案件',
      'payment_received': '收款',
      'payment_pending': '收款',
      'review_new': '評價',
      'system_maintenance': '系統',
      'system_update': '系統',
      'certification_approved': '認證',
      'certification_rejected': '認證',
      'security_alert': '安全',
      'promotion': '活動',
      'message_new': '消息',
      'technician_assignment': '排班',
      'schedule_confirmed': '排程',
      // ✅ 添加新的通知类型
      'verification_approved': '驗收',
      'verification_disputed': '驗收',
      'verification_admin_followup': '驗收',
      'verification_dispute_mediated': '驗收',
      'service_in_progress': '服務狀態',
      'service_on_the_way': '服務狀態',
      'service_completed': '服務狀態',
    };

    return categoryMap[type] || '其他';
  }

  // 根據通知類型獲取優先級
  static getNotificationPriority(type: string): 'low' | 'normal' | 'high' | 'urgent' {
    const priorityMap: Record<string, 'low' | 'normal' | 'high' | 'urgent'> = {
      'job_new': 'high',
      'job_accepted': 'normal',
      'job_completed': 'normal',
      'job_cancelled': 'normal',
      'payment_received': 'high',
      'payment_pending': 'normal',
      'review_new': 'normal',
      'system_maintenance': 'high',
      'system_update': 'low',
      'certification_approved': 'high',
      'certification_rejected': 'high',
      'security_alert': 'urgent',
      'promotion': 'low',
      'message_new': 'normal',
      'technician_assignment': 'high',
      'schedule_confirmed': 'high',
      'verification_admin_followup': 'high',
      'verification_dispute_mediated': 'high',
    };

    return priorityMap[type] || 'normal';
  }

  // 訂閱即時通知
  static subscribeToNotifications(
    userId: string, 
    callback: (payload: any) => void
  ): () => void {
    try {
      // 使用唯一的 channel 名稱，避免重複訂閱
      const channelName = `notifications:${userId}:${Date.now()}`;
      
      // 先嘗試移除可能存在的舊 channel
      const existingChannel = supabase.getChannels().find(
        ch => ch.topic.startsWith(`notifications:${userId}:`)
      );
      if (existingChannel) {
        console.log('🔔 發現已存在的通知訂閱，先移除舊訂閱');
        supabase.removeChannel(existingChannel);
      }
      
      // 訂閱 Supabase 即時更新
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`
          },
          (payload) => {
            console.log('收到通知更新:', payload);
            
            // 如果是新通知，依 user_preferences（推播／勿擾）決定是否顯示瀏覽器通知
            if (payload.eventType === 'INSERT' && payload.new) {
              void this.maybeShowBrowserNotificationForUser(
                userId,
                payload.new.title,
                payload.new.message,
                payload.new.type
              );
            }
            
            callback(payload);
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log('✅ 通知訂閱成功:', channelName);
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ 通知訂閱失敗:', channelName);
          }
        });

      // 返回取消訂閱函數
      return () => {
        console.log('🔔 取消通知訂閱:', channelName);
        supabase.removeChannel(channel);
      };
    } catch (error) {
      console.error('訂閱通知失敗:', error);
      return () => {}; // 返回空函數避免錯誤
    }
  }

  // 請求瀏覽器通知權限
  static async requestNotificationPermission(): Promise<boolean> {
    try {
      if (!('Notification' in window)) {
        console.log('此瀏覽器不支援通知功能');
        return false;
      }

      if (Notification.permission === 'granted') {
        return true;
      }

      if (Notification.permission === 'denied') {
        console.log('通知權限已被拒絕');
        return false;
      }

      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('請求通知權限失敗:', error);
      return false;
    }
  }

  /** 依帳號偏好（推播開關、勿擾、緊急類型）決定是否顯示桌面通知 */
  static async maybeShowBrowserNotificationForUser(
    userId: string,
    title: string,
    message: string,
    type: string
  ): Promise<void> {
    try {
      const prefs = await UserPreferencesService.getUserPreferences(userId);
      const priority = this.getNotificationPriority(type);
      if (!shouldShowBrowserPushNotification(prefs, { type, priority })) {
        return;
      }
      if (Capacitor.isNativePlatform()) {
        showForegroundPushBanner({ title, message, type });
        return;
      }
      this.showBrowserNotification(title, message, type);
    } catch (e) {
      console.warn('讀取通知偏好失敗，仍嘗試顯示桌面通知:', e);
      if (Capacitor.isNativePlatform()) {
        showForegroundPushBanner({ title, message, type });
        return;
      }
      this.showBrowserNotification(title, message, type);
    }
  }

  // 顯示瀏覽器通知
  static showBrowserNotification(
    title: string, 
    message: string, 
    type: string = 'info'
  ): void {
    try {
      if (Notification.permission !== 'granted') {
        return;
      }

      const options: NotificationOptions = {
        body: message,
        icon: '/launchericon-96x96.png', // 可以根據通知類型設置不同圖標
        badge: '/launchericon-96x96.png',
        tag: `notification-${Date.now()}`, // 避免重複通知
        requireInteraction: this.getNotificationPriority(type) === 'urgent',
        silent: this.getNotificationPriority(type) === 'low'
      };

      const notification = new Notification(title, options);

      // 點擊通知時的處理
      notification.onclick = () => {
        window.focus();
        notification.close();
        
        // 可以根據通知類型導航到相應頁面
        window.location.href = getNotificationOpenPath(type);
      };

      // 自動關閉通知
      setTimeout(() => {
        notification.close();
      }, 5000);

    } catch (error) {
      console.error('顯示瀏覽器通知失敗:', error);
    }
  }
}